import { UltravoxSession } from 'ultravox-client';

interface ChatbotClientOptions {
  agentId: string;
  apiKey: string;
  onConnecting?: () => void;
  onConnected?: () => void;
  onDisconnecting?: () => void;
  onDisconnected?: () => void;
  onError?: (error: Error) => void;
  onBotTranscript?: (transcript: any) => void;
  onUserTranscript?: (transcript: any) => void;
  onStatusChange?: (status: string) => void;
}

class ChatbotClient {
  private uvSession: UltravoxSession | null = null;
  private agentId: string;
  private apiKey: string;
  private onConnecting?: () => void;
  private onConnected?: () => void;
  private onDisconnecting?: () => void;
  private onDisconnected?: () => void;
  private onError?: (error: Error) => void;
  private onBotTranscript?: (transcript: any) => void;
  private onUserTranscript?: (transcript: any) => void;
  private onStatusChange?: (status: string) => void;
  private backgroundAudio: HTMLAudioElement;
  private botAudio: HTMLAudioElement;
  private audioContext?: AudioContext;

  constructor(options: ChatbotClientOptions) {
    this.agentId = options.agentId;
    this.apiKey = options.apiKey;
    this.onConnecting = options.onConnecting;
    this.onConnected = options.onConnected;
    this.onDisconnecting = options.onDisconnecting;
    this.onError = options.onError;
    this.onDisconnected = options.onDisconnected;
    this.onBotTranscript = options.onBotTranscript;
    this.onUserTranscript = options.onUserTranscript;
    this.onStatusChange = options.onStatusChange;

    this.backgroundAudio = new Audio('/MARKOVICHAMP-Borghestral.mp3');
    this.backgroundAudio.loop = true;
    this.backgroundAudio.volume = 0.3;

    this.botAudio = new Audio();
    this.botAudio.autoplay = true;
    (this.botAudio as any).playsInline = true;
    this.botAudio.controls = false;
    this.botAudio.muted = false;
    this.botAudio.volume = 1.0;

    // Set up global error suppression for Ultravox SDK bugs
    this.setupErrorSuppression();

    // Add error handling for audio elements
    this.botAudio.addEventListener('error', e => {
      console.error('Bot audio element error:', e);
    });

    this.backgroundAudio.addEventListener('error', e => {
      console.warn('Background music error:', e);
    });
  }

  // Global error suppression for Ultravox SDK bugs
  private setupErrorSuppression() {
    const originalError = window.console.error;
    window.console.error = (...args) => {
      const message = args.join(' ');

      // Skip logging specific Ultravox SDK errors
      if (
        message.includes(
          'Unexpected token \'o\', "[object Blob]" is not valid JSON'
        ) ||
        message.includes("Failed to construct 'URL': Invalid URL") ||
        message.includes('ultravox-client.js')
      ) {
        return; // Suppress these specific errors
      }

      // Log all other errors normally
      originalError.apply(console, args);
    };

    // Global error handler for unhandled promise rejections
    window.addEventListener('unhandledrejection', event => {
      const message = event.reason?.message || event.reason || '';

      if (
        message.includes(
          'Unexpected token \'o\', "[object Blob]" is not valid JSON'
        ) ||
        message.includes("Failed to construct 'URL': Invalid URL")
      ) {
        console.warn('🔇 Suppressed known Ultravox SDK bug:', message);
        event.preventDefault();
        return false;
      }
    });

    // Global error handler for synchronous errors
    window.addEventListener('error', event => {
      const message = event.message || '';

      if (
        message.includes(
          'Unexpected token \'o\', "[object Blob]" is not valid JSON'
        ) ||
        message.includes("Failed to construct 'URL': Invalid URL")
      ) {
        console.warn('🔇 Suppressed known Ultravox SDK bug:', message);
        event.preventDefault();
        return false;
      }
    });

    console.log('✅ Error suppression for Ultravox SDK bugs activated');
  }

  // Initialize audio context after user gesture
  private async initializeAudioContext(): Promise<void> {
    try {
      if (window.AudioContext || (window as any).webkitAudioContext) {
        const AudioContextClass =
          window.AudioContext || (window as any).webkitAudioContext;

        if (!this.audioContext) {
          this.audioContext = new AudioContextClass();
          console.log('✅ Audio context created');
        }

        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
          console.log('✅ Audio context resumed');
        }

        console.log('✅ Audio context state:', this.audioContext.state);
      }

      // Request microphone permissions
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        console.log('✅ Microphone permissions granted');
        // Stop the stream immediately as we just needed permissions
        stream.getTracks().forEach(track => track.stop());
      }
    } catch (audioError) {
      console.warn('⚠️ Audio initialization warning:', audioError);
      // Continue anyway - Ultravox might handle this internally
    }
  }

  // Start background music
  private startBackgroundMusic(): void {
    this.backgroundAudio.play().catch(error => {
      console.warn('Could not play background music:', error);
    });
  }

  // Stop background music
  private stopBackgroundMusic(): void {
    this.backgroundAudio.pause();
    this.backgroundAudio.currentTime = 0;
  }

  private log(message: string): void {
    console.log(message);
  }

  async connect(): Promise<void> {
    try {
      this.onConnecting?.();

      // Start background music when connecting
      this.startBackgroundMusic();

      // Initialize audio context after user click
      await this.initializeAudioContext();

      // Get join URL from your API
      const res = await fetch(
        `https://prod.quibbleai.io:3002/api/getJoinUrl?key=${this.apiKey}&agentId=${this.agentId}`
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const responseData = await res.json();
      const { joinUrl, callId } = responseData;

      console.log('✅ Parsed joinUrl:', joinUrl);
      console.log('✅ Ultravox callId:', callId);

      if (!joinUrl || !joinUrl.startsWith('wss://')) {
        throw new Error('Invalid joinUrl received from server');
      }

      // Clean up any existing session
      if (this.uvSession) {
        try {
          await this.uvSession.leaveCall();
        } catch (err) {
          console.warn('Error cleaning up previous session:', err);
        }
      }

      // Create new session
      this.uvSession = new UltravoxSession();

      // Set audio element BEFORE setting up event listeners
      (this.uvSession as any).audioElement = this.botAudio;

      // Set up event listeners - simplified to use actual Ultravox states
      this.uvSession.addEventListener('status', () => {
        const status = this.uvSession?.status;
        this.log(`🛰️ Status: ${status}`);

        // Pass the actual status to the callback
        this.onStatusChange?.(String(status || 'disconnected'));

        // Convert status to string for comparison
        const statusStr = String(status);
        switch (statusStr) {
          case 'connecting':
            this.log('🔗 Connecting to call...');
            this.onConnecting?.();
            break;
          case 'idle':
            this.log('🎯 Call is ready - bot should speak soon');
            this.stopBackgroundMusic();
            this.onConnected?.();
            break;
          case 'listening':
            this.log('🎤 Bot is listening - you can speak now!');
            this.stopBackgroundMusic();
            break;
          case 'speaking':
            this.log('🗣️ Bot is speaking');
            this.stopBackgroundMusic();
            break;
          case 'thinking':
            this.log('🤔 Bot is processing your speech');
            break;
          case 'disconnecting':
            this.log('🔌 Disconnecting...');
            this.onDisconnecting?.();
            break;
          case 'disconnected':
            this.log('❌ Disconnected');
            this.onDisconnected?.();
            this.stopBackgroundMusic();
            break;
          default:
            this.log(`❓ Unknown status: ${status}`);
            break;
        }
      });

      this.uvSession.addEventListener('transcripts', () => {
        const transcripts = this.uvSession?.transcripts;
        this.log(`📝 Transcripts received: ${transcripts?.length || 0}`);

        if (transcripts && transcripts.length > 0) {
          // Process all transcripts, not just the latest one
          transcripts.forEach(transcript => {
            if (transcript && transcript.text) {
              // Handle both final and non-final transcripts
              if (transcript.isFinal) {
                this.log(`${transcript.speaker} (final): ${transcript.text}`);
              } else {
                this.log(`${transcript.speaker} (partial): ${transcript.text}`);
              }

              // Call appropriate transcript callback based on speaker
              if (transcript.speaker === 'agent') {
                this.onBotTranscript?.({
                  text: transcript.text,
                  timestamp: Date.now(),
                  final: transcript.isFinal,
                });
              } else if (transcript.speaker === 'user') {
                this.onUserTranscript?.({
                  text: transcript.text,
                  timestamp: Date.now(),
                  final: transcript.isFinal,
                });
              }
            }
          });
        }
      });

      this.uvSession.addEventListener('error', event => {
        console.error('❌ Ultravox Session Error:', event);
        this.onError?.(new Error('Ultravox session error'));
        this.stopBackgroundMusic();
        this.disconnect().catch(err =>
          console.error('Error during cleanup:', err)
        );
      });

      // Join the call
      console.log('🔗 Joining call with URL:', joinUrl);
      this.uvSession.joinCall(joinUrl);

      this.log('✅ Successfully joined call');
    } catch (error) {
      this.log(`❌ Error: ${(error as Error).message}`);
      console.error('Full error:', error);
      this.stopBackgroundMusic();
      this.onError?.(error as Error);
      await this.disconnect().catch(err =>
        console.error('Error during cleanup:', err)
      );
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    try {
      this.onDisconnecting?.();

      // Stop background music
      this.stopBackgroundMusic();

      if (this.uvSession) {
        try {
          this.log('🔌 Disconnecting from call...');
          await this.uvSession.leaveCall();
          this.log('✅ Successfully disconnected');
        } catch (err) {
          console.warn('Error during disconnect:', err);
        }
        this.uvSession = null;
      }

      // Clean up audio properly
      this.botAudio.pause();
      this.botAudio.currentTime = 0;
      if (this.botAudio.src) {
        this.botAudio.removeAttribute('src');
        this.botAudio.load(); // Reset the audio element
      }

      this.onDisconnected?.();
    } catch (error) {
      console.error('Disconnection error:', error);
      throw error;
    }
  }

  isConnected(): boolean {
    if (!this.uvSession || !this.uvSession.status) {
      return false;
    }

    // Convert enum to string explicitly before comparison
    const statusString = this.uvSession.status.toString();
    return statusString !== 'disconnected' && statusString !== 'disconnecting';
  }

  // Get current status
  getStatus(): string {
    if (!this.uvSession || !this.uvSession.status) {
      return 'disconnected';
    }
    return this.uvSession.status.toString();
  }
}

export default ChatbotClient;
